import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/config/api";
import { ArrowLeft, Plus, Edit, FileText } from "lucide-react";
import NewCustomer from "./NewCustomer";
import { useAuth } from "@/contexts/AuthContext";
import { showError } from "@/utils/dialog";
import { addDaysToLocalDateInputValue, toLocalDateInputValue } from "@/utils/date";

export default function ViewCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const canAdjust = ["admin", "finance"].includes(role);
  const canEditCustomer = ["admin", "finance", "sales"].includes(role);
  const canCreateOrder = ["admin", "sales"].includes(role);
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [statement, setStatement] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [postingAdjustment, setPostingAdjustment] = useState(false);
  const [adjustment, setAdjustment] = useState({
    action: "add",
    amount: "",
    adjustment_date: toLocalDateInputValue(),
    notes: "",
  });

  useEffect(() => {
    if (id) {
      Promise.all([
        api.customers.getById(id),
        api.orders.getAll({ customer_id: id }),
        api.invoices.getAll({ customer_id: id }),
        api.reports.statement("customer", id).catch(() => null),
        api.payments.getAll().catch(() => []),
      ])
        .then(([c, o, inv, st, allPayments]) => {
          setCustomer(c);
          setOrders(o || []);
          setInvoices(inv || []);
          setStatement(st || null);
          setPayments(allPayments || []);
        })
        .catch((err) => {
          console.error(err);
          if (err.message?.includes("404") || err.message?.includes("not found")) {
            navigate("/customers");
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id, navigate]);

  const openCount = orders.filter((o) => o.status === "open").length;
  const closedCount = orders.filter((o) => o.status === "closed").length;
  const paidInvoices = invoices.filter((i) => i.payment_status === "paid").length;
  const unpaidInvoices = invoices.filter(
    (i) => i.payment_status === "unpaid" || i.payment_status === "partial"
  ).length;

  const handleEditSuccess = () => {
    setEditing(false);
    api.customers.getById(id).then(setCustomer);
  };

  const invoiceIds = useMemo(() => new Set(invoices.map((inv) => inv.id)), [invoices]);
  const customerPayments = useMemo(
    () => payments.filter((pay) => invoiceIds.has(pay.invoice_id)),
    [payments, invoiceIds],
  );
  const refundRows = useMemo(
    () =>
      customerPayments.filter(
        (pay) => String(pay.transaction_type || "").toLowerCase() === "refund",
      ),
    [customerPayments],
  );
  const totalInvoiced = Number(customer?.total_invoiced || 0);
  const totalCollected = Number(customer?.amount_paid || 0);
  const totalRefunded = refundRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const currentReceivable = Number(customer?.amount_to_receive || 0);
  const customerCreditBalance = Number(customer?.customer_credit_balance || 0);
  const netReceivableBalance = Number(
    customer?.net_receivable_balance != null
      ? customer.net_receivable_balance
      : currentReceivable - customerCreditBalance,
  );
  const invoiceTotals = useMemo(
    () =>
      invoices.reduce(
        (acc, inv) => {
          const total = Number(inv.total || 0);
          const paid = Number(inv.amount_paid || 0);
          acc.total += total;
          acc.paid += paid;
          acc.pending += Math.max(0, total - paid);
          return acc;
        },
        { total: 0, paid: 0, pending: 0 },
      ),
    [invoices],
  );
  const paymentTotals = useMemo(
    () =>
      customerPayments.reduce(
        (acc, row) => {
          const amount = Number(row.amount || 0);
          if (String(row.transaction_type || "").toLowerCase() === "refund") acc.refunds += amount;
          else acc.payments += amount;
          return acc;
        },
        { payments: 0, refunds: 0 },
      ),
    [customerPayments],
  );

  const aging = useMemo(() => {
    const now = new Date();
    const buckets = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
    invoices.forEach((inv) => {
      const pending = Number(inv.total || 0) - Number(inv.amount_paid || 0);
      if (pending <= 0) return;
      const due = inv.due_date ? new Date(inv.due_date) : new Date(inv.created_at);
      const days = Math.floor((now - due) / (1000 * 60 * 60 * 24));
      if (days <= 30) buckets["0-30"] += pending;
      else if (days <= 60) buckets["31-60"] += pending;
      else if (days <= 90) buckets["61-90"] += pending;
      else buckets["90+"] += pending;
    });
    return buckets;
  }, [invoices]);

  if (loading || !customer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (editing) {
    return (
      <NewCustomer
        customer={customer}
        onClose={() => setEditing(false)}
        onSuccess={handleEditSuccess}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/customers")}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            title="Back to Customers"
          >
            <ArrowLeft size={20} className="text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-secondary">
              {customer.name}
            </h1>
            <p className="text-stone-600 text-sm">
              Enrollment:{" "}
              {new Date(customer.created_at).toLocaleDateString(undefined, {
                dateStyle: "medium",
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEditCustomer && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50"
            >
              <Edit size={18} />
              Edit
            </button>
          )}
          {canCreateOrder && (
            <Link
              to={`/orders/new?customer_id=${customer.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              <Plus size={18} />
              New Order
            </Link>
          )}
          {!canEditCustomer && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/workflow-tickets?source_type=other&source_id=${encodeURIComponent(customer.id)}&to_role=management&title=Customer%20update%20required&description=Please%20review%20and%20update%20customer%20details.&due_date=${addDaysToLocalDateInputValue(1)}`,
                )
              }
              className="inline-flex items-center gap-2 px-4 py-2 border border-indigo-300 text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100"
            >
              <FileText size={16} />
              Generate Ticket
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-4">
            Contact details
          </h2>
          <div className="space-y-2 text-stone-700">
            {customer.email && (
              <p>
                <span className="text-stone-500">Email:</span> {customer.email}
              </p>
            )}
            {customer.phone && (
              <p>
                <span className="text-stone-500">Phone:</span> {customer.phone}
              </p>
            )}
            {customer.company && (
              <p>
                <span className="text-stone-500">Company:</span> {customer.company}
              </p>
            )}
            {customer.address && (
              <p>
                <span className="text-stone-500">Address:</span> {customer.address}
              </p>
            )}
            {!customer.email && !customer.phone && !customer.company && !customer.address && (
              <p className="text-stone-500">No contact details</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-4">Orders & invoices</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="text-2xl font-semibold text-secondary">{orders.length}</p>
              <p className="text-sm text-stone-600">Total orders</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-2xl font-semibold text-amber-700">{openCount}</p>
              <p className="text-sm text-stone-600">Open orders</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-2xl font-semibold text-emerald-700">{closedCount}</p>
              <p className="text-sm text-stone-600">Closed orders</p>
            </div>
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="text-2xl font-semibold text-secondary">{invoices.length}</p>
              <p className="text-sm text-stone-600">Invoices</p>
            </div>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <span className="text-emerald-600">{paidInvoices} paid</span>
            <span className="text-amber-600">{unpaidInvoices} unpaid/partial</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
        <div className="bg-black text-white px-4 py-2 font-bold tracking-wide">
          CUSTOMER FINANCIAL SUMMARY
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <div className="p-3 rounded-lg bg-stone-50">
            <p className="text-xs text-stone-500">Opening balance</p>
            <p className="text-lg font-semibold text-stone-800">
              {Number(customer.opening_balance || 0).toFixed(2)}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-sky-50">
            <p className="text-xs text-stone-500">Total invoiced</p>
            <p className="text-lg font-semibold text-sky-700">{totalInvoiced.toFixed(2)}</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50">
            <p className="text-xs text-stone-500">Total collected</p>
            <p className="text-lg font-semibold text-emerald-700">{totalCollected.toFixed(2)}</p>
          </div>
          <div className="p-3 rounded-lg bg-rose-50">
            <p className="text-xs text-stone-500">Total refunded</p>
            <p className="text-lg font-semibold text-rose-700">{totalRefunded.toFixed(2)}</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50">
            <p className="text-xs text-stone-500">Current receivable</p>
            <p className="text-lg font-semibold text-amber-700">{currentReceivable.toFixed(2)}</p>
          </div>
          <div className="p-3 rounded-lg bg-cyan-50">
            <p className="text-xs text-stone-500">Customer credit balance</p>
            <p className="text-lg font-semibold text-cyan-700">
              {customerCreditBalance.toFixed(2)}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-violet-50">
            <p className="text-xs text-stone-500">Net receivable balance</p>
            <p className="text-lg font-semibold text-violet-700">
              {netReceivableBalance.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {canAdjust && (
        <div className="bg-white rounded-xl border border-stone-300 shadow-sm p-4 space-y-3">
          <h3 className="font-semibold text-stone-800">Customer balance adjustment</h3>
          <p className="text-xs text-stone-500">
            Use Add/Withdraw for controlled receivable adjustments with audit trail.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-xs text-stone-600 mb-1">Action</label>
              <select
                value={adjustment.action}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, action: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              >
                <option value="add">Add</option>
                <option value="withdraw">Withdraw</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={adjustment.amount}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, amount: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Date</label>
              <input
                type="date"
                value={adjustment.adjustment_date}
                onChange={(e) =>
                  setAdjustment((prev) => ({
                    ...prev,
                    adjustment_date: e.target.value,
                  }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Notes</label>
              <input
                type="text"
                value={adjustment.notes}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, notes: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <button
              type="button"
              disabled={postingAdjustment}
              onClick={async () => {
                setPostingAdjustment(true);
                try {
                  await api.accounting.createBalanceAdjustment({
                    party_type: "customer",
                    party_id: id,
                    action: adjustment.action,
                    amount: Number(adjustment.amount || 0),
                    adjustment_date: adjustment.adjustment_date,
                    notes: adjustment.notes,
                  });
                  setAdjustment((prev) => ({ ...prev, amount: "", notes: "" }));
                  const [c, st, allPayments] = await Promise.all([
                    api.customers.getById(id),
                    api.reports.statement("customer", id).catch(() => null),
                    api.payments.getAll().catch(() => []),
                  ]);
                  setCustomer(c);
                  setStatement(st || null);
                  setPayments(allPayments || []);
                } catch (e) {
                  await showError(e.message || "Failed to post adjustment");
                } finally {
                  setPostingAdjustment(false);
                }
              }}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {postingAdjustment ? "Posting..." : "Post adjustment"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {Object.entries(aging).map(([bucket, value]) => (
          <div key={bucket} className="bg-white rounded-xl border border-stone-300 shadow-sm p-4">
            <p className="text-xs font-semibold text-stone-600 uppercase">{bucket} days aging</p>
            <p className="text-xl font-semibold text-stone-800">{Number(value || 0).toFixed(2)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
        <div className="bg-amber-200 px-4 py-2 font-bold text-stone-900 tracking-wide">
          INVOICES
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-y border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Invoice</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Date</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Total</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Paid</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Pending</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-stone-500">No invoices yet.</td>
                </tr>
              ) : (
                invoices.slice(0, 10).map((inv) => (
                  <tr key={inv.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <td className="py-3 px-4 font-medium text-stone-800">{inv.invoice_number || "—"}</td>
                    <td className="py-3 px-4 text-stone-700">
                      {new Date(inv.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">{Number(inv.total || 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono">{Number(inv.amount_paid || 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono">
                      {Math.max(0, Number(inv.total || 0) - Number(inv.amount_paid || 0)).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-stone-700">{inv.payment_status || inv.status}</td>
                    <td className="py-3 px-4 text-right">
                      <Link to={`/invoices/${inv.id}`} className="text-primary hover:underline text-sm">
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {invoices.length > 0 && (
              <tfoot>
                <tr className="bg-black text-white font-semibold">
                  <td className="py-2 px-4" colSpan={2}>TOTAL</td>
                  <td className="py-2 px-4 text-right font-mono">{invoiceTotals.total.toFixed(2)}</td>
                  <td className="py-2 px-4 text-right font-mono">{invoiceTotals.paid.toFixed(2)}</td>
                  <td className="py-2 px-4 text-right font-mono">{invoiceTotals.pending.toFixed(2)}</td>
                  <td className="py-2 px-4" />
                  <td className="py-2 px-4" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
        <div className="bg-amber-200 px-4 py-2 font-bold text-stone-900 tracking-wide">
          STATEMENT (CUSTOMER LEDGER)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-y border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Description</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Debit</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Credit</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Running Balance</th>
              </tr>
            </thead>
            <tbody>
              {!statement?.transactions?.length ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-stone-500">No ledger transactions yet.</td>
                </tr>
              ) : (
                statement.transactions.slice(-15).map((row, idx) => (
                  <tr key={`${row.id || "row"}-${idx}`} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <td className="py-3 px-4 text-stone-700">
                      {row.entry_date
                        ? new Date(row.entry_date).toLocaleDateString(undefined, { dateStyle: "medium" })
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-stone-700">{row.entry_type || "—"}</td>
                    <td className="py-3 px-4 text-stone-700">{row.description || "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{Number(row.debit || 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono">{Number(row.credit || 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold">
                      {Number(row.running_balance || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {statement?.transactions?.length > 0 && (
              <tfoot>
                <tr className="bg-black text-white font-semibold">
                  <td className="py-2 px-4" colSpan={3}>CLOSING BALANCE</td>
                  <td className="py-2 px-4 text-right font-mono">{Number(statement?.total_debit || 0).toFixed(2)}</td>
                  <td className="py-2 px-4 text-right font-mono">{Number(statement?.total_credit || 0).toFixed(2)}</td>
                  <td className="py-2 px-4 text-right font-mono">{Number(statement?.closing_balance || 0).toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
        <div className="bg-amber-200 px-4 py-2 font-bold text-stone-900 tracking-wide">
          PAYMENTS & REFUNDS
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-y border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Invoice</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Mode</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Reference</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Amount</th>
              </tr>
            </thead>
            <tbody>
              {customerPayments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-stone-500">No payments/refunds found.</td>
                </tr>
              ) : (
                customerPayments.slice(0, 20).map((row) => (
                  <tr key={row.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <td className="py-3 px-4 text-stone-700">
                      {row.payment_date
                        ? new Date(row.payment_date).toLocaleDateString(undefined, { dateStyle: "medium" })
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-stone-700">{row.invoice_number || "—"}</td>
                    <td className="py-3 px-4 text-stone-700">{row.transaction_type || "payment"}</td>
                    <td className="py-3 px-4 text-stone-700">{row.mode_of_payment || "—"}</td>
                    <td className="py-3 px-4 text-stone-700">{row.reference_number || "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{Number(row.amount || 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {customerPayments.length > 0 && (
              <tfoot>
                <tr className="bg-black text-white font-semibold">
                  <td className="py-2 px-4" colSpan={4}>TOTALS</td>
                  <td className="py-2 px-4 text-right">Payments</td>
                  <td className="py-2 px-4 text-right font-mono">{paymentTotals.payments.toFixed(2)}</td>
                </tr>
                <tr className="bg-black text-white font-semibold border-t border-stone-600">
                  <td className="py-2 px-4" colSpan={4} />
                  <td className="py-2 px-4 text-right">Refunds</td>
                  <td className="py-2 px-4 text-right font-mono">{paymentTotals.refunds.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <h2 className="text-lg font-semibold text-secondary p-6 pb-0">Recent orders</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-y border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-stone-500">
                    No orders yet. Create one with &quot;New Order&quot;.
                  </td>
                </tr>
              ) : (
                orders.slice(0, 10).map((order) => (
                  <tr key={order.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <td className="py-3 px-4 text-stone-700">
                      {new Date(order.created_at).toLocaleDateString(undefined, {
                        dateStyle: "medium",
                      })}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          order.status === "closed"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-primary hover:underline text-sm"
                      >
                        View
                      </Link>
                      {order.status === "closed" && order.invoice_id && (
                        <Link
                          to={`/invoices/${order.invoice_id}`}
                          className="ml-3 inline-flex items-center gap-1 text-emerald-600 hover:underline text-sm"
                        >
                          <FileText size={14} />
                          Invoice
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
